import { useMemo, useRef, useState } from 'react'
import type {
  ProjectContextComposeLearnedItem,
  ProjectContextResponse,
  ProjectContextTerm,
  UpdateProjectContextRequest,
} from '@ai-meeting/shared'
import {
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  DownloadIcon,
  PlusIcon,
  SearchIcon,
  SparklesIcon,
} from '@/components/Icons'
import SelectField from '@/components/SelectField'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { formatDate } from '@/utils/formatDateTime'
import { parseDictionaryCsv } from '../projectDictionaryCsv'
import {
  reviewAppearedLine,
  reviewConfidenceLabel,
  reviewConfidenceTier,
} from '../projectDictionaryReviewMeta'
import {
  blockLearnedProjectTerm,
  isProjectTermBlocked,
  projectDictionaryTermKey,
  unblockLearnedProjectTerm,
} from '../projectDictionaryTerms'
import AddDictionaryTermsModal from './AddDictionaryTermsModal'
import DeleteDictionaryTermModal from './DeleteDictionaryTermModal'
import EditDictionaryTermModal from './EditDictionaryTermModal'
import ProjectDictionaryTermsTable, {
  type DictionaryRow,
} from './ProjectDictionaryTermsTable'

type SourceFilter = 'all' | 'manual' | 'ai'

/** `origin` the AI service writes on a suggested (review-only) learned term. */
const LEARNED_SUGGESTION_ORIGIN = 'MEETING_SUGGESTION'

type Props = {
  context: ProjectContextResponse
  saving: boolean
  errorMessage?: string | null
  onSave: (body: UpdateProjectContextRequest) => Promise<unknown>
}

function termKey(source: string, targetLang: string): string {
  return projectDictionaryTermKey(source, targetLang)
}

/**
 * Project dictionary: declared ACTIVE terms plus AI-learned ones, and the review
 * list for the suggestions that have not been decided on yet.
 */
export default function ProjectDictionaryPanel({
  context,
  saving,
  errorMessage = null,
  onSave,
}: Props) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [addOpen, setAddOpen] = useState(false)
  const [editingTerm, setEditingTerm] = useState<ProjectContextTerm | null>(null)
  const [deletingTerm, setDeletingTerm] = useState<ProjectContextTerm | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  const declaredActive = useMemo(
    () => context.terms.filter((term) => term.status !== 'BLOCKED'),
    [context.terms]
  )

  const learnedRows = useMemo((): ProjectContextComposeLearnedItem[] => {
    if (context.preview?.learned?.length) return context.preview.learned
    // Without a compose verdict, the AI's own tag still tells a suggestion
    // (never used on its own) from a term waiting for its second meeting.
    return context.learnedTerms.map((term) => ({
      source: term.source,
      target: term.target ?? null,
      targetLang: term.targetLang,
      status:
        term.origin === LEARNED_SUGGESTION_ORIGIN ? ('suggested' as const) : ('pending' as const),
      meetings: term.meetingIds?.length ?? 0,
      meetingIds: term.meetingIds ?? [],
      lastSeenAt: term.lastSeenAt ?? null,
    }))
  }, [context.preview, context.learnedTerms])

  const rows = useMemo((): DictionaryRow[] => {
    const q = search.trim().toLowerCase()
    const out: DictionaryRow[] = []

    if (sourceFilter !== 'ai') {
      for (const term of declaredActive) {
        if (
          q &&
          !term.source.toLowerCase().includes(q) &&
          !(term.target ?? '').toLowerCase().includes(q)
        ) {
          continue
        }
        out.push({
          kind: 'manual',
          key: `manual:${termKey(term.source, term.targetLang)}`,
          term,
        })
      }
    }

    if (sourceFilter !== 'manual') {
      const declaredKeys = new Set(
        declaredActive.map((term) => termKey(term.source, term.targetLang))
      )
      for (const learned of learnedRows) {
        // Only what the next meeting already receives. Everything else is still
        // waiting in the review list, and drawing it in both places reads as two
        // different terms.
        if (learned.status !== 'used') continue
        if (declaredKeys.has(termKey(learned.source, learned.targetLang))) continue
        if (
          q &&
          !learned.source.toLowerCase().includes(q) &&
          !(learned.target ?? '').toLowerCase().includes(q)
        ) {
          continue
        }
        out.push({
          kind: 'learned',
          key: `learned:${termKey(learned.source, learned.targetLang)}`,
          learned,
          blocked: isProjectTermBlocked(context.terms, learned.source, learned.targetLang),
        })
      }
    }

    return out
  }, [declaredActive, learnedRows, search, sourceFilter, context.terms])

  /**
   * Suggestions still waiting on a decision: everything the AI learned that the
   * declared half does not already carry. Blocked ones stay here, because this
   * list is the only place the off switch can be turned back on.
   */
  const reviewRows = useMemo(() => {
    const declaredKeys = new Set(
      declaredActive.map((term) => termKey(term.source, term.targetLang))
    )
    return learnedRows.filter(
      (learned) =>
        learned.status !== 'used' &&
        !declaredKeys.has(termKey(learned.source, learned.targetLang))
    )
  }, [declaredActive, learnedRows])

  async function persistTerms(terms: ProjectContextTerm[]) {
    await onSave({
      text: context.text,
      general: context.general,
      terms,
    })
  }

  async function handleAddTerms(newTerms: ProjectContextTerm[]) {
    const existingKeys = new Set(
      context.terms.map((term) => termKey(term.source, term.targetLang))
    )
    const merged = [...context.terms]
    for (const term of newTerms) {
      const key = termKey(term.source, term.targetLang)
      if (existingKeys.has(key)) {
        const idx = merged.findIndex((row) => termKey(row.source, row.targetLang) === key)
        if (idx >= 0) merged[idx] = term
      } else {
        merged.push(term)
        existingKeys.add(key)
      }
    }
    await persistTerms(merged)
  }

  async function handleEditTerm(updated: ProjectContextTerm) {
    if (!editingTerm) return
    const oldKey = termKey(editingTerm.source, editingTerm.targetLang)
    const withoutOld = context.terms.filter(
      (row) => termKey(row.source, row.targetLang) !== oldKey
    )
    const withoutDup = withoutOld.filter(
      (row) =>
        termKey(row.source, row.targetLang) !== termKey(updated.source, updated.targetLang)
    )
    await persistTerms([...withoutDup, updated])
  }

  async function handleDeleteTerm(term: ProjectContextTerm) {
    await persistTerms(
      context.terms.filter(
        (row) => termKey(row.source, row.targetLang) !== termKey(term.source, term.targetLang)
      )
    )
  }

  /** Writes a learned term into the declared half, keeping the translation. */
  async function handleAddLearned(learned: ProjectContextComposeLearnedItem) {
    await handleAddTerms([
      learned.target
        ? {
            source: learned.source,
            target: learned.target,
            targetLang: learned.targetLang,
            status: 'ACTIVE',
          }
        : { source: learned.source, targetLang: learned.targetLang, status: 'ACTIVE' },
    ])
    // Declared terms only appear in the main table under “All sources”.
    setSourceFilter('all')
  }

  async function handleBlock(learned: ProjectContextComposeLearnedItem) {
    await persistTerms(blockLearnedProjectTerm(context.terms, learned))
  }

  async function handleUnblock(learned: ProjectContextComposeLearnedItem) {
    await persistTerms(unblockLearnedProjectTerm(context.terms, learned))
  }

  async function handleImportCsv(file: File) {
    setImportError(null)
    const terms = parseDictionaryCsv(await file.text())
    if (terms.length === 0) {
      setImportError(t.projectDetailDictImportEmpty)
      return
    }
    await handleAddTerms(terms)
  }

  function reviewSubtitle(learned: ProjectContextComposeLearnedItem): string {
    const lastSeen = learned.lastSeenAt
      ? formatDate(learned.lastSeenAt, uiLocale)
      : null
    return reviewAppearedLine(learned.meetings, lastSeen, t)
  }

  return (
    <div className="flex flex-col gap-6" data-testid="project-dictionary-panel">
      <section className="flex flex-col rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 shadow-[var(--shadow-sm)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            <label className="relative min-w-0 flex-1 sm:max-w-[236px]">
              <span className="sr-only">{t.projectDetailDictSearchPlaceholder}</span>
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.projectDetailDictSearchPlaceholder}
                className="input-field h-8 w-full rounded-lg py-1.5 pl-9 pr-3 text-xs"
              />
            </label>
            <div className="w-full shrink-0 sm:w-40 [&_select]:h-8 [&_select]:rounded-lg [&_select]:py-1 [&_select]:text-xs">
              <SelectField
                label={t.projectDetailDictSourceAll}
                labelHidden
                value={sourceFilter}
                onChange={(value) => setSourceFilter(value as SourceFilter)}
              >
                <option value="all">{t.projectDetailDictSourceAll}</option>
                <option value="manual">{t.projectDetailDictSourceManual}</option>
                <option value="ai">{t.projectDetailDictSourceAi}</option>
              </SelectField>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) void handleImportCsv(file)
              }}
            />
            <button
              type="button"
              disabled={saving}
              onClick={() => fileInput.current?.click()}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
            >
              <DownloadIcon className="h-3.5 w-3.5" />
              {t.projectDetailDictImportCsv}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setAddOpen(true)}
              className="btn-primary inline-flex h-8 items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {t.projectDetailDictAddTerm}
            </button>
          </div>
        </div>

        {(importError || errorMessage) && (
          <p role="alert" className="mt-3 text-xs text-[var(--error)]">
            {importError ?? errorMessage}
          </p>
        )}

        <div className="mt-1">
          {rows.length === 0 &&
          declaredActive.length === 0 &&
          learnedRows.length === 0 ? (
            <p className="py-4 text-sm text-[var(--text-tertiary)]">
              {t.projectDetailDictionaryEmpty}
            </p>
          ) : (
            <ProjectDictionaryTermsTable
              rows={rows}
              disabled={saving}
              onEditManual={setEditingTerm}
              onDeleteManual={setDeletingTerm}
              onAddLearned={(learned) => void handleAddLearned(learned)}
              onBlockLearned={(learned) => void handleBlock(learned)}
              onUnblockLearned={(learned) => void handleUnblock(learned)}
            />
          )}
        </div>
      </section>

      <section
        data-testid="project-dictionary-review"
        className="flex flex-col gap-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 shadow-[var(--shadow-sm)]"
      >
        <h2 className="text-sm font-bold tracking-tight text-[var(--text-primary)]">
          {t.projectDetailDictReviewTitle}
        </h2>

        {reviewRows.length === 0 ? (
          <p className="text-xs text-[var(--text-tertiary)]">
            {t.projectDetailDictReviewEmpty}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {reviewRows.map((learned) => {
              const blocked = isProjectTermBlocked(
                context.terms,
                learned.source,
                learned.targetLang
              )
              const confidence = reviewConfidenceTier(learned.meetings)
              return (
                <li
                  key={termKey(learned.source, learned.targetLang)}
                  className="flex flex-col gap-3 rounded-xl border border-[var(--border-color)]/80 bg-[var(--bg-secondary)] p-[15px] shadow-[var(--shadow-sm)] sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f3ff] text-[var(--accent)] dark:bg-violet-500/15">
                      <SparklesIcon className="h-4 w-4" />
                    </span>
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--text-primary)]">
                          {learned.source}
                        </span>
                        <ArrowRightIcon className="h-3 w-3 shrink-0 text-[var(--text-tertiary)]" />
                        <span className="text-xs text-[var(--text-secondary)]">
                          {learned.target ?? '—'}
                        </span>
                        <span className="rounded bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                          {reviewConfidenceLabel(confidence, t)}
                        </span>
                      </div>
                      <span className="text-[11px] leading-snug text-[var(--text-tertiary)]">
                        {reviewSubtitle(learned)}
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {blocked ? (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void handleUnblock(learned)}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
                      >
                        <ArrowUturnLeftIcon className="h-3 w-3" />
                        {t.projectDetailDictReviewUnblock}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void handleAddLearned(learned)}
                          className="btn-primary inline-flex h-8 items-center rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                        >
                          {t.projectDetailDictReviewAdd}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void handleBlock(learned)}
                          className="inline-flex h-8 items-center rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
                        >
                          {t.projectDetailDictReviewSkip}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <AddDictionaryTermsModal
        open={addOpen}
        disabled={saving}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAddTerms}
      />
      <EditDictionaryTermModal
        term={editingTerm}
        disabled={saving}
        onClose={() => setEditingTerm(null)}
        onSubmit={handleEditTerm}
      />
      <DeleteDictionaryTermModal
        term={deletingTerm}
        disabled={saving}
        onClose={() => setDeletingTerm(null)}
        onConfirm={handleDeleteTerm}
      />
    </div>
  )
}
